import React from 'react';
import type { GetServerSideProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Head from 'next/head';

import { BuyUsedCar as BuyUsedCarView } from 'components/views/BuyUsedCar';
import { faq } from 'content/faq';
import { StrapiService } from 'lib/strapi/StrapiService';
import { MagazineArticleCard } from 'components/sections/Magazine';
import { withSessionSsr } from 'lib/auth';

interface FormData {
  licensePlate: string;
  mileage: string;
}

const validationSchema = yup
  .object({
    licensePlate: yup
      .string()
      .matches(/^[A-Za-z]{3}\d{2}[A-Za-z\d]$/, 'Vänligen fyll i ett korrekt registreringsnummer')
      .label('Registreringsnummer'),
  })
  .required();

interface Props {
  articles: MagazineArticleCard[];
}

const BuyUsedCar: NextPage<Props> = ({ articles }) => {
  const router = useRouter();

  const canonicalUrl = `https://${process.env.NEXT_PUBLIC_SITE_DOMAIN}${router.asPath}`;
  const methods = useForm<FormData>({ resolver: yupResolver(validationSchema) });
  const onSubmit = (data: FormData) => {
    router.push(
      {
        pathname: '/kopa-begagnad-bil/[licensePlate]',
        query: { licensePlate: data.licensePlate, mileage: data.mileage },
      },
      '/kopa-begagnad-bil/[licensePlate]'
    );
  };

  return (
    <FormProvider {...methods}>
      <Head>
        <title>Kolla registreringsnumret kostnadsfritt - Blipp</title>
        <meta property="og:title" content="Kolla registreringsnumret kostnadsfritt - Blipp" />
        <meta
          property="og:description"
          content="Få fördelarna som hos bilhandlaren fast till priset hos privatpersonen. Välkommen till den nya bilaffären utan mellanhänder!"
        />
        <meta
          name="description"
          content="Få fördelarna som hos bilhandlaren fast till priset hos privatpersonen. Välkommen till den nya bilaffären utan mellanhänder!"
        />
        <link rel="canonical" key="canonical" href={canonicalUrl} />
      </Head>

      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <BuyUsedCarView articles={articles} faq={faq} />
      </form>
    </FormProvider>
  );
};

export const getServerSideProps: GetServerSideProps = withSessionSsr(async () => {
  const articles = await StrapiService.getLatestMagazineArticles(3);

  return {
    props: {
      articles,
    },
  };
});

export default BuyUsedCar;
